import React, { useCallback, useContext, useEffect, useMemo } from 'react';

import { StepsContext, ActiveStepContext, UpdateActiveStepContext, HandleNextContext, HandlePreviousContext, SubmitRefContext, UpdateSavedContext, FilteredStepsContext, NextButtonTargetContext, PurchaseLoadingContext } from '../context';
import { useTranslate } from './i18n-hooks';
import { NumberedStep } from '../types';

export const useFilteredSteps = () => useContext(FilteredStepsContext)!;
export const useSteps = () => useContext(StepsContext)!;
export const useActiveStep = () => useContext(ActiveStepContext)!;
export const useUpdateActiveStep = () => useContext(UpdateActiveStepContext)!;
export const useHandleNext = () => useContext(HandleNextContext)!;
export const useHandlePrevious = () => useContext(HandlePreviousContext)!;
export const useSubmitRef = () => useContext(SubmitRefContext)!;
export const useUpdateSaved = () => useContext(UpdateSavedContext)!;
export const useNextButtonTarget = () => useContext(NextButtonTargetContext)!;
export const usePurchaseLoading = () => useContext(PurchaseLoadingContext)!;

export const useNubmeredSteps = (): NumberedStep[] => {
	const purchaseSteps = useFilteredSteps();
	const steps = useSteps();
	const translate = useTranslate()

	const numberedSteps = useMemo<NumberedStep[]>(() => {
		return steps
			.map((step, index) => ({
				...step,
				index,
				label: translate(purchaseSteps[index].label || purchaseSteps[index].title)
			}))
			.filter(step => step.visible);
	}, [steps, translate]);

	return numberedSteps;
}

export const useStepTitle = (): string => {
	const purchaseSteps = useFilteredSteps();
	const activeStep = useActiveStep();
	const translate = useTranslate()

	const stepTitle = useMemo(() => {
		return translate(purchaseSteps[activeStep].title);
	}, [activeStep, translate]);

	return stepTitle;
}

export const useCurrentComponent = (): React.ComponentType<any> => {
	const activeStep = useActiveStep();
	const purchaseSteps = useFilteredSteps();

	const component = useMemo(() => purchaseSteps[activeStep].component, [activeStep]);

	return component;
}

export const useUpdateSubmit = (submit: (isBackButton?: boolean) => Promise<void>) => {
	const submitRef = useSubmitRef();

	useEffect(() => {
		if (!submitRef) return;
		submitRef.current = submit;
	}, [submit]);

	useEffect(() => {
		if (!submitRef) return;
		return () => {
			submitRef.current = null;
		}
	}, []);
}

export const useHideNavigation = (): boolean => {
	const activeStep = useActiveStep();
	const steps = useSteps();

	const hideNavigation = useMemo(() => {
		return steps[activeStep].hideNavigation;
	}, [activeStep, steps]);

	return hideNavigation;
}

export const useActiveVisibleStep = () => {
	const activeStep = useActiveStep();
	const steps = useSteps();

	const activeVisibleStep = useMemo<number>(() => {
		const stepArray = steps.map((step, index) => step.visible ? index === activeStep ? 'activeStep' : 'otherStep' : undefined);
		const visibleSteps = stepArray.filter(step => !!step);

		const activeVisibleStep = visibleSteps.indexOf('activeStep');

		return activeVisibleStep;
	}, [activeStep, steps]);

	return activeVisibleStep;
}

export const useSetActiveStep = () => {
	const updateActiveStep = useUpdateActiveStep();

	const setActiveStep = useCallback((step: number) => {
		updateActiveStep(async () => step);
	}, [updateActiveStep]);

	return setActiveStep;
}

export const useIsForNeedy = (): boolean => {
	const steps = useSteps();

	const isForNeedy = useMemo(() => {
		return !steps[2].visible;
	}, [steps]);

	return isForNeedy;
}